/**
 * Emits PlantUML text for the ArchiMate notation, using PlantUML's
 * `archimate/Archimate` standard library. Each concept becomes a procedure
 * call (`Application_Component(id, "label")`); each relation becomes a
 * `Rel_*` call. Layout is left to PlantUML — positions in our view_state
 * are not part of the textual output.
 *
 * The emitter is pure: takes concepts + relations + a UI language and
 * returns a string. Used by the worker render-proxy and the live text
 * pane in the web editor.
 */
import { legacyTypeIdFor } from "../archimate4Migration";
import type { BilingualText, Concept, Relation } from "../types";

// Procedure names from the plantuml-stdlib Archimate-PlantUML library
// (https://github.com/plantuml-stdlib/Archimate-PlantUML/blob/master/README.md).
// Format is `Category_TypeName` in CamelCase — most multi-word types
// concatenate the words without underscores (e.g. `ValueStream`,
// `CommunicationNetwork`, `DataObject`, `WorkPackage`, `SystemSoftware`).
export const TYPE_PROCEDURE: Record<string, string> = {
  // Strategy
  "archimate.Resource": "Strategy_Resource",
  "archimate.Capability": "Strategy_Capability",
  "archimate.ValueStream": "Strategy_ValueStream",
  "archimate.CourseOfAction": "Strategy_CourseOfAction",
  // Business
  "archimate.BusinessActor": "Business_Actor",
  "archimate.BusinessRole": "Business_Role",
  "archimate.BusinessCollaboration": "Business_Collaboration",
  "archimate.BusinessInterface": "Business_Interface",
  "archimate.BusinessProcess": "Business_Process",
  "archimate.BusinessFunction": "Business_Function",
  "archimate.BusinessService": "Business_Service",
  "archimate.BusinessObject": "Business_Object",
  "archimate.BusinessEvent": "Business_Event",
  "archimate.Representation": "Business_Representation",
  "archimate.Contract": "Business_Contract",
  "archimate.Location": "Business_Location",
  // `Meaning` woont in de Motivation-laag in de ArchiMate-PlantUML stdlib
  // — niet `Business_Meaning` (bestaat niet in kroki's bundled stdlib).
  "archimate.Meaning": "Motivation_Meaning",
  "archimate.Product": "Business_Product",
  // `archimate.UseCase` heeft geen eigen procedure in de archimate-stdlib
  // (UseCase is UML, geen canonical ArchiMate-type). Bewust niet gemapped
  // → emitter valt terug op `' unknown type` comment-pad.
  // Application
  "archimate.ApplicationComponent": "Application_Component",
  "archimate.ApplicationInterface": "Application_Interface",
  "archimate.ApplicationFunction": "Application_Function",
  "archimate.ApplicationProcess": "Application_Process",
  "archimate.ApplicationService": "Application_Service",
  "archimate.DataObject": "Application_DataObject",
  "archimate.ApplicationCollaboration": "Application_Collaboration",
  "archimate.ApplicationEvent": "Application_Event",
  "archimate.ApplicationInteraction": "Application_Interaction",
  // Technology
  "archimate.Node": "Technology_Node",
  "archimate.Device": "Technology_Device",
  "archimate.SystemSoftware": "Technology_SystemSoftware",
  "archimate.CommunicationNetwork": "Technology_CommunicationNetwork",
  "archimate.TechnologyInterface": "Technology_Interface",
  "archimate.TechnologyService": "Technology_Service",
  "archimate.Artifact": "Technology_Artifact",
  // Physical-laag (ArchiMate 3.2) — eigen `Physical_*`-procedures in de
  // archimate-PlantUML stdlib. Niet `Technology_*` (die procedures bestaan
  // niet en faalden voorheen met kroki-400).
  "archimate.DistributionNetwork": "Physical_DistributionNetwork",
  "archimate.Equipment": "Physical_Equipment",
  "archimate.Facility": "Physical_Facility",
  "archimate.Material": "Physical_Material",
  "archimate.Path": "Technology_Path",
  // Implementation & Migration
  "archimate.Plateau": "Implementation_Plateau",
  "archimate.Gap": "Implementation_Gap",
  "archimate.WorkPackage": "Implementation_WorkPackage",
  "archimate.Deliverable": "Implementation_Deliverable",
  "archimate.ImplementationEvent": "Implementation_Event",
  // Connectors (And/Or junction — geen layer-prefix in plantuml-stdlib)
  "archimate.AndJunction": "Junction_And",
  "archimate.OrJunction": "Junction_Or",
  // Motivation
  "archimate.Stakeholder": "Motivation_Stakeholder",
  "archimate.Driver": "Motivation_Driver",
  "archimate.Assessment": "Motivation_Assessment",
  "archimate.Goal": "Motivation_Goal",
  "archimate.Outcome": "Motivation_Outcome",
  "archimate.Principle": "Motivation_Principle",
  "archimate.Requirement": "Motivation_Requirement",
  "archimate.Constraint": "Motivation_Constraint",
  "archimate.Value": "Motivation_Value",
  // Composite
  // De ArchiMate-PlantUML stdlib (plantuml-stdlib) heeft ÉÉN procedure
  // genaamd `Grouping($alias, $label)` voor de archimate-grouping-vorm.
  // Eerdere namen die we hebben gebruikt en geen geldige stdlib-naam
  // bleken: `Other_Grouping` (legacy van een oudere fork) en
  // `Grouping_Group` (uit Archi-exports / Claude-output, niet uit de
  // stdlib). Beide worden door de parser geaccepteerd als alias zodat
  // pasted-from-elders content blijft werken.
  "archimate.Grouping": "Grouping",
  // C4-extensies — geen eigen plantuml-stdlib-procedure, dus mappen op
  // hun parent_type_id-procedure (zie c4-pack.json). Zonder deze
  // mappings zou de emitter ze als `' unknown type c4.Person: …`
  // comment wegschrijven, waarna PlantUML een ghost-knoopje creëert
  // bij elke relatie die de alias gebruikt.
  "c4.Person": "Business_Actor",
  "c4.SoftwareSystemInstance": "Application_Component",
  "c4.ContainerInstance": "Application_Component",
  "c4.InfrastructureNode": "Technology_Node",
  // Sharp Concept Modelling-types zonder ArchiMate-equivalent — fallback
  // op data-ish primitieven (Business_Object) zodat de PlantUML-render
  // tenminste een vorm toont. De semantiek staat in de stereotype-
  // comment-trailer als `includeStereotypeComments: true` actief is.
  // Zie docs/architecture/plantuml-aanhechting.md → "Sharp Concept
  // Modelling — render-strategie keuze 2 (ArchiMate-fallback)".
  "sharp.Attribute": "Business_Object",
  "sharp.Identifier": "Business_Object",
};

/**
 * De procedure voor een concept, via z'n 3.x-identiteit.
 *
 * **Waarom deze omweg nodig is.** PlantUML's Archimate-bibliotheek is
 * 3.x-only — er ís geen `Process()`-procedure, alleen `Business_Process()`,
 * `Application_Process()`, enzovoort. Precies dezelfde situatie als bij de
 * AMEFF-export, dus dezelfde oplossing: `legacyTypeIdFor` vertaalt terug via
 * het compat-profiel, valt voor een born-v4 concept terug op de gepubliceerde
 * default, en laat een nooit-gemergd type ongemoeid.
 *
 * Zonder dit gaf `TYPE_PROCEDURE[c.typeId]` `undefined` voor elk gemigreerd
 * concept, en werd dat in `emitConcept` een commentaarregel — het concept
 * verdween uit zowel het tekstpaneel als de Kroki-render.
 *
 * Ook gebruikt door `applyParseResult` om te bepalen of een teruggelezen
 * type écht een wijziging is: de parser ziet `Business_Process` en meldt
 * `archimate.BusinessProcess`, terwijl het concept `archimate.Process` is.
 * Beide kanten door deze resolver halen maakt van die schijn-wijziging een
 * no-op — zonder dat zou elke tekst-edit het concept naar een gedeactiveerd
 * type hertyperen.
 */
export function procedureForConcept(
  typeId: string,
  stereotypes: readonly string[] = [],
): string | undefined {
  return TYPE_PROCEDURE[legacyTypeIdFor(typeId, stereotypes)] ?? TYPE_PROCEDURE[typeId];
}

export const RELATION_PROCEDURE: Record<string, string> = {
  "archimate.Composition": "Rel_Composition",
  "archimate.Aggregation": "Rel_Aggregation",
  "archimate.Assignment": "Rel_Assignment",
  "archimate.Realization": "Rel_Realization",
  "archimate.Serving": "Rel_Serving",
  "archimate.Access": "Rel_Access",
  "archimate.Triggering": "Rel_Triggering",
  "archimate.Flow": "Rel_Flow",
  "archimate.Influence": "Rel_Influence",
  "archimate.Specialization": "Rel_Specialization",
  "archimate.Association": "Rel_Association",
};

export interface EmitOptions {
  /** Preferred display language for labels. Falls back to `nl`, then any. */
  lang?: string;
  /** Optional title rendered as `title "…"` at the top of the diagram. */
  title?: string;
  /**
   * Per-relation handle ids (`top|right|bottom|left`) for source/target
   * endpoints. Source-handle wordt vertaald naar
   * `Rel_<Type>_<Up|Down|Left|Right>`-suffix zodat PlantUML een
   * richting-hint krijgt voor de auto-layout. Target-handle wordt nog
   * niet gebruikt (stdlib heeft geen target-side suffix-variant).
   *
   * Voorheen werd ook een trailing `' @handles source=… target=…`-
   * comment geëmit voor roundtrip-parse-preservatie; verwijderd
   * 2026-05-24 omdat canvas canonical is en PlantUML het comment
   * zelf negeert (pure visuele ruis in het Onderwater venster). De
   * parser accepteert het comment-format nog wel voor geplakte
   * PlantUML uit oudere snapshots of andere tools.
   */
  handles?: Record<string, { source?: string | undefined; target?: string | undefined }>;
  /**
   * Per-relation bend-points (canvas-waypoints). De emitter zet ze om
   * in een chain van hidden rectangles + plain PlantUML-pijlen zodat
   * ELK een knik in de lijn maakt op de waypoint-positie. Zie
   * docs/architecture/plantuml-aanhechting.md "Stap B". Lege array of
   * undefined → geen chain, normale `Rel_<Type>(from, to)`-emit.
   *
   * Coördinaten worden NIET gebruikt voor richting-keuze in de
   * gegenereerde pijlen (we laten ELK de routing doen); ze tellen
   * alleen voor het aantal waypoints en daarmee het aantal hidden
   * rectangles + segmenten.
   */
  waypoints?: Record<string, ReadonlyArray<{ x: number; y: number }>>;
  /**
   * Per-concept "is visually nested inside …" map. Key = child concept id,
   * value = parent concept id. De emitter zet dit om in PlantUML brace-
   * blocks (`<TypeProc>(alias, "…") { … kinderen … }`) zodat de visuele
   * hiërarchie zichtbaar wordt na render. Alle archimate-procedures
   * accepteren brace-blocks (Application_Component, Technology_Node,
   * Grouping, …); we slaan alleen drawing-primitieven over want die
   * worden als comment geëmit en kunnen geen container vormen. Een
   * containment-edge waarvan beide endpoints niet in `concepts` voorkomen
   * wordt gewoon overgeslagen — geen brace-block, kind komt op top-level.
   */
  containment?: Record<string, string>;
  /**
   * Voor Sharp-views (sharp.*-view-kinds): append een
   * `' «<stereotype-id>»`-comment aan elke concept-regel die actieve
   * stereotypes heeft. Sharp Concept Modelling heeft géén eigen
   * PlantUML-library; we fallback'en op ArchiMate-stdlib + stereotype-
   * comments om de betekenis tekstueel zichtbaar te houden ondanks
   * dat de visuele rendering ArchiMate-symboliek toont. Zie
   * docs/architecture/plantuml-aanhechting.md → "Sharp Concept
   * Modelling — render-strategie keuze 2 (ArchiMate-fallback)".
   *
   * Bij `false` (default): geen comments. Bij `true`: alle stereotypes
   * worden in een regel-trailing `' «s1» «s2» …`-comment opgenomen.
   */
  includeStereotypeComments?: boolean;
  /**
   * PlantUML lijn-routing-keuze. Bepaalt welke `skinparam linetype …`
   * regel in de preamble komt:
   *   - `"ortho"`     → `skinparam linetype ortho`  (rechte hoeken,
   *                     default behavior)
   *   - `"polyline"`  → `skinparam linetype polyline` (rechte segmenten,
   *                     mag schuin)
   *   - `"default"`   → géén skinparam (PlantUML's curved/spline default)
   *
   * Default = `"ortho"` voor backward-compat met de huidige rendering.
   */
  lineType?: "default" | "polyline" | "ortho";
  /**
   * Per-instance emit-modus (multi-instance-refactor 2026-05-26). Caller
   * levert per concept-id een lijst van entry-keys op het diagram. Voor
   * elke entry-key emit'd de emitter een aparte Business_Object-regel
   * met alias `<short>_<n>` (eerste entry behoudt de korte alias zonder
   * suffix). Relaties krijgen source/target = de alias van de gepinde
   * instance via `relationInstancePins` (zie volgend veld). Wanneer
   * `instancesByConcept` ontbreekt of voor een concept geen entries
   * bevat: fallback op één Business_Object-regel zoals voorheen
   * (logical view).
   */
  instancesByConcept?: Record<string, ReadonlyArray<string>>;
  /**
   * Per-relation lijst van visuele occurrences. Eén item per viewState-
   * relation-entry: relation-id + gepinde source/target entry-keys.
   * Wanneer aanwezig én niet-leeg: emitter emit'd één Rel_-regel per
   * item (multi-instance: meerdere lijnen tussen dezelfde concept-pair
   * met verschillende instance-aliassen). Wanneer leeg/ontbreekt:
   * fallback op één regel per relation uit `visibleRelations` (logical
   * view).
   */
  relationOccurrences?: ReadonlyArray<{
    relationId: string;
    sourceEntryKey?: string;
    targetEntryKey?: string;
  }>;
}

export function emitArchiMatePlantUML(
  concepts: readonly Concept[],
  relations: readonly Relation[],
  options: EmitOptions = {},
): string {
  const lang = options.lang ?? "nl";
  const lines: string[] = [];
  lines.push("@startuml");
  lines.push("!include <archimate/Archimate>");
  // Layout + routing-instellingen die de PlantUML-render dichter bij de
  // canvas-layout houden. Alleen `skinparam linetype ortho` — dwingt
  // orthogonale lijnen die op het midden van een zijde aansluiten,
  // niet schuin op een hoek.
  //
  // `!pragma layout elk` (Eclipse Layout Kernel) is BEWUST WEGGELATEN
  // omdat kroki's PlantUML-distributie de ELK-jar niet bundelt. Render
  // crashte met `ClassNotFoundException: ElkGraphUtil` + "Sorry, ELK
  // intregration is really alpha feature…". Zonder ELK respecteert de
  // default-dot-engine de `Rel_<Type>_<Direction>`-suffix-hints minder
  // strikt, maar de diagram blijft renderen. De directional-suffix in
  // de emit-output blijft staan zodat we 'm zonder code-wijziging
  // terug kunnen activeren mocht kroki ooit een ELK-build krijgen
  // (of bij overstap naar plantuml.com-server zelf).
  //
  // Zie docs/architecture/plantuml-aanhechting.md voor de motivatie.
  const lineType = options.lineType ?? "ortho";
  if (lineType === "ortho") lines.push("skinparam linetype ortho");
  else if (lineType === "polyline") lines.push("skinparam linetype polyline");
  // "default" → géén skinparam, PlantUML gebruikt curved/spline routing
  lines.push("");
  if (options.title) {
    lines.push(`title "${escapeQuotes(options.title)}"`);
    lines.push("");
  }

  const visibleConcepts = concepts.filter((c) => !c.tombstoneInBranch);
  const visibleRelations = relations.filter((r) => !r.tombstoneInBranch);

  // Build containment tree so we can emit nested concepts as brace-
  // blocks (`<TypeProc>(alias, "…") { … children … }`). Test heeft
  // bevestigd dat de plantuml-stdlib elke archimate-procedure accepteert
  // als brace-block-parent (Application_Component, Technology_Node,
  // Grouping, …) — dus we beperken niet langer tot Grouping. Alleen
  // drawing.* sluiten we uit (die zijn als comment geëmit, brace-block
  // op een comment is geen geldige PlantUML).
  const conceptById = new Map<string, Concept>();
  for (const c of visibleConcepts) conceptById.set(c.id, c);
  const childrenByParent = new Map<string, Concept[]>();
  const parentByChild = new Map<string, string>();
  if (options.containment) {
    for (const [childId, parentId] of Object.entries(options.containment)) {
      const child = conceptById.get(childId);
      const parent = conceptById.get(parentId);
      if (!child || !parent) continue;
      // Drawing-primitieven worden als comment geëmit; geen brace-block
      // mogelijk. Hieronder skippen we beide kanten.
      if (parent.typeId.startsWith("drawing.")) continue;
      if (child.typeId.startsWith("drawing.")) continue;
      parentByChild.set(childId, parentId);
      const arr = childrenByParent.get(parentId) ?? [];
      arr.push(child);
      childrenByParent.set(parentId, arr);
    }
  }
  // Preserve the input ordering of children inside each block.
  for (const arr of childrenByParent.values()) {
    arr.sort((a, b) => visibleConcepts.indexOf(a) - visibleConcepts.indexOf(b));
  }

  // Roots = concepts that aren't claimed as a child of some Grouping.
  const roots = visibleConcepts.filter((c) => !parentByChild.has(c.id));

  // Per-instance-mode helper: bouw een mapping entryKey → alias.
  // Eerste entry per concept houdt de korte alias (= sanitize(conceptId)),
  // 2e+ krijgen suffix `_N`. Wanneer instancesByConcept ontbreekt voor
  // een concept: één alias zonder suffix (= huidige logical view).
  const aliasByEntryKey = new Map<string, string>();
  const entryKeysByConcept = options.instancesByConcept ?? {};
  for (const c of visibleConcepts) {
    const entries = entryKeysByConcept[c.id];
    if (!entries || entries.length === 0) {
      // Logical view: één alias = entry-key === concept-id.
      aliasByEntryKey.set(c.id, sanitizeId(c.id));
      continue;
    }
    entries.forEach((ek, idx) => {
      const baseAlias = sanitizeId(c.id);
      aliasByEntryKey.set(ek, idx === 0 ? baseAlias : `${baseAlias}_${idx + 1}`);
    });
  }
  const aliasFor = (entryKey: string, fallbackConceptId: string): string => {
    return aliasByEntryKey.get(entryKey) ?? sanitizeId(fallbackConceptId);
  };

  const emitConcept = (c: Concept, depth: number): void => {
    const proc = procedureForConcept(c.typeId, c.stereotypes);
    const rawLabel = escapeQuotes(resolveBilingual(c.name, lang));
    const isJunction = proc === "Junction_And" || proc === "Junction_Or";
    const label = isJunction && rawLabel === "" ? " " : rawLabel;
    const indent = "  ".repeat(depth);
    if (!proc) {
      lines.push(`${indent}' unknown type ${c.typeId}: ${sanitizeId(c.id)} "${rawLabel}"`);
      return;
    }
    const hasStereoComment =
      options.includeStereotypeComments && c.stereotypes && c.stereotypes.length > 0;
    // Per-instance: één regel per entry-key voor deze concept-id.
    // Bij ontbreken (logical view): één regel met alias = concept-id.
    const entries = entryKeysByConcept[c.id];
    const renderInstances: ReadonlyArray<string> = entries && entries.length > 0 ? entries : [c.id];
    for (const ek of renderInstances) {
      const alias = aliasFor(ek, c.id);
      if (hasStereoComment && c.stereotypes) {
        lines.push(`${indent}' ${c.stereotypes.map((s) => `«${s}»`).join(" ")}`);
      }
      const kids = childrenByParent.get(c.id);
      if (kids && kids.length > 0 && renderInstances.length === 1) {
        // Containment-brace-block alleen bij logical view (één instance);
        // multi-instance kindshapes binnen brace-blocks zou de PlantUML-
        // syntax complexer maken zonder dat de modelleur per visuele
        // instance een eigen containment-structuur heeft. Bij multi-
        // instance vallen we terug op flat-emit.
        lines.push(`${indent}${proc}(${alias}, "${label}") {`);
        for (const k of kids) emitConcept(k, depth + 1);
        lines.push(`${indent}}`);
      } else {
        lines.push(`${indent}${proc}(${alias}, "${label}")`);
      }
    }
  };

  for (const c of roots) emitConcept(c, 0);

  if (visibleConcepts.length > 0 && visibleRelations.length > 0) {
    lines.push("");
  }

  // Composition / Aggregation: this app stores `source = part, target = whole`
  // so the diamond renders at the target end. PlantUML stdlib's
  // `Rel_Composition(whole, part)` expects the opposite, so swap arguments.
  const SWAP_FOR_PLANTUML = new Set(["archimate.Composition", "archimate.Aggregation"]);

  // Per-instance / logical: bouw lijst van te emitten relatie-
  // occurrences. Per-instance: caller levert `relationOccurrences`,
  // één item per viewState-entry. Logical: één item per relation
  // (sourceEntryKey/targetEntryKey blijven undefined → canonical alias).
  const relationOccurrences =
    options.relationOccurrences && options.relationOccurrences.length > 0
      ? options.relationOccurrences
      : visibleRelations.map((r) => ({
          relationId: r.id,
          sourceEntryKey: undefined as string | undefined,
          targetEntryKey: undefined as string | undefined,
        }));
  const relationById = new Map(visibleRelations.map((r) => [r.id, r] as const));
  for (const occ of relationOccurrences) {
    const r = relationById.get(occ.relationId);
    if (!r) continue;
    const proc = RELATION_PROCEDURE[r.typeId] ?? "Rel_Association";
    const swap = SWAP_FOR_PLANTUML.has(r.typeId);
    const sourceConceptId = swap ? r.targetConceptId : r.sourceConceptId;
    const targetConceptId = swap ? r.sourceConceptId : r.targetConceptId;
    const pinnedSrc = swap ? occ.targetEntryKey : occ.sourceEntryKey;
    const pinnedTgt = swap ? occ.sourceEntryKey : occ.targetEntryKey;
    const fromId =
      pinnedSrc !== undefined && aliasByEntryKey.has(pinnedSrc)
        ? aliasFor(pinnedSrc, sourceConceptId)
        : sanitizeId(sourceConceptId);
    const toId =
      pinnedTgt !== undefined && aliasByEntryKey.has(pinnedTgt)
        ? aliasFor(pinnedTgt, targetConceptId)
        : sanitizeId(targetConceptId);
    const label = escapeQuotes(resolveBilingual(r.label, lang));
    const waypointsForR = options.waypoints?.[r.id];

    // Bend-point-chain via hidden rectangles. Wanneer de canvas-relatie
    // bend-points heeft (modelleur sleepte 'm in een specifieke route),
    // emit'en we per waypoint een onzichtbaar `rectangle wp_<relId>_<i>`
    // + plain `-->`-pijl. ELK respecteert dan de knikken visueel, en
    // de parser skipt de wp_-aliases (zie archimate-parser).
    // We slaan in dit pad de directionele Rel_<Type>_<Direction>-emit
    // over want de chain is plain PlantUML; de handle-comment voegen we
    // wel toe aan de eerste segment-regel zodat roundtrip de handle-info
    // (canvas-aanhechtzijde) niet verliest.
    if (waypointsForR && waypointsForR.length > 0) {
      const relAlias = sanitizeId(r.id);
      const aliases = waypointsForR.map((_, i) => `wp_${relAlias}_${i + 1}`);
      for (const alias of aliases) {
        lines.push(`rectangle ${alias}`);
      }
      // Eerste segment: source → eerste waypoint.
      const firstSeg = label
        ? `${fromId} --> ${aliases[0]} : "${label}"`
        : `${fromId} --> ${aliases[0]}`;
      lines.push(firstSeg);
      // Tussensegmenten: waypoint → waypoint.
      for (let i = 0; i < aliases.length - 1; i++) {
        lines.push(`${aliases[i]} --> ${aliases[i + 1]}`);
      }
      // Laatste segment: laatste waypoint → target.
      lines.push(`${aliases[aliases.length - 1]} --> ${toId}`);
      // Verberg de waypoint-rectangles.
      for (const alias of aliases) {
        lines.push(`hide ${alias}`);
      }
      continue;
    }

    // Geen waypoints — normale `Rel_<Type>_<Direction>`-emit (directional
    // suffix op basis van source-handle, zie commit 0f4f56a).
    //
    // NB: het `' @handles source=… target=…`-comment dat we hier tot
    // 2026-05-24 ook nog meegestuurde is verwijderd. Canvas is canonical
    // bij ons (PlantUML-render is read-only-output, paste-back gaat via
    // de parser die nieuwe relaties maakt zonder handle-info). De
    // dirSuffix bouwt de positionele hint al ín de procedure-naam zelf;
    // het comment was alleen een roundtrip-overblijfsel dat niemand
    // gebruikt — pure visuele ruis in het Onderwater venster.
    const dirSuffix = directionSuffix(options.handles?.[r.id]?.source);
    const procWithDir = `${proc}${dirSuffix}`;
    const line = label
      ? `${procWithDir}(${fromId}, ${toId}, "${label}")`
      : `${procWithDir}(${fromId}, ${toId})`;
    lines.push(line);
  }

  lines.push("");
  lines.push("@enduml");
  return lines.join("\n");
}

// ---- Helpers --------------------------------------------------------------

/**
 * PlantUML aliases must be alphanumeric + underscore. UUIDs contain hyphens,
 * so prefix with `n_` and replace hyphens with underscores.
 */
function sanitizeId(id: string): string {
  return `n_${id.replace(/-/g, "_")}`;
}

function escapeQuotes(s: string): string {
  // PlantUML string-literals kunnen niet over fysieke regels heen — een
  // letterlijke newline in `name` of `description` breekt de parser
  // (syntax error op de verdwaalde tweede regel). Converteer \n/\r naar
  // de PlantUML-line-break-escape `\n` (twee karakters: backslash + n)
  // zodat de tekst als multi-line label binnen het element rendert.
  return s
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\r\n/g, "\\n")
    .replace(/[\r\n]/g, "\\n");
}

/**
 * Map viewState-handle (left/right/top/bottom) naar de
 * stdlib-procedure-suffix (`_Up` / `_Down` / `_Left` / `_Right`) zodat
 * ELK een richting-hint krijgt. Onbekende of ontbrekende handle ⇒ geen
 * suffix (PlantUML/ELK kiest zelf).
 */
function directionSuffix(sourceHandle: string | undefined): string {
  switch (sourceHandle) {
    case "right":
      return "_Right";
    case "left":
      return "_Left";
    case "top":
      return "_Up";
    case "bottom":
      return "_Down";
    default:
      return "";
  }
}

function resolveBilingual(text: BilingualText, preferred: string): string {
  return text[preferred] ?? text.nl ?? text.en ?? Object.values(text)[0] ?? "";
}
